import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Camera, Save, X, Loader2, Sparkles, Frame, Image as ImageIcon, ShoppingBag, LogOut } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getAuthToken, getPortalType, getUserData, setAuthTokens, clearAuthTokens, clearAllSessions } from '@/utils/auth';
import { apiUrl } from '@/api/config';
import { cn } from '@/lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { shopAPI } from '@/api/shop';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';

export default function ProfilePage() {
  const [userProfile, setUserProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [fullName, setFullName] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const fileInputRef = useRef(null);
  const [inventory, setInventory] = useState([]);
  const [equippedItems, setEquippedItems] = useState({
    CHARACTER: null,
    AVATAR: null,
    FRAME: null,
  });
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [equipping, setEquipping] = useState(null);
  const [useProfilePicAsAvatar, setUseProfilePicAsAvatar] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const navigate = useNavigate();

  const portal = getPortalType(window.location.pathname || '');

  // Load user profile data
  const loadProfile = async () => {
    try {
      const token = getAuthToken(portal);
      if (!token) {
        setIsLoading(false);
        return;
      }

      // Fetch profile from backend to get latest data including use_profile_pic_as_avatar
      const response = await axios.get(apiUrl('users/profile/'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const userData = response.data;

      // Get profile photo from backend
      let photoUrl = null;
      const photoField = userData.profile_photo || userData.profile_pic_url || userData.photo || userData.avatar;

      if (photoField) {
        if (photoField.startsWith('http://') || photoField.startsWith('https://')) {
          photoUrl = photoField;
        } else {
          const cleanPath = photoField.startsWith('/') ? photoField.slice(1) : photoField;
          photoUrl = apiUrl(cleanPath);
        }
      }

      const profile = {
        photo: photoUrl,
        fullName: userData.full_name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.username || 'User',
        username: userData.username?.includes('__') ? userData.username.split('__')[1] : (userData.username || ''),
        schoolName: userData.school?.school_name || userData.school_name || null,
        syllabus: userData.board?.board_name || userData.syllabus || null,
        grade: userData.grade?.grade_name || userData.grade_name || null,
        division: userData.division?.division_name || userData.division_display?.division_name || null,
        userId: userData.id || userData.user_id || null,
        useProfilePicAsAvatar: userData.use_profile_pic_as_avatar || false,
        userType: userData.user_type || userData.userType || null
      };

      setUserProfile(profile);
      setFullName(profile.fullName);
      setProfilePhotoPreview(photoUrl);
      // Load preference from backend
      setUseProfilePicAsAvatar(userData.use_profile_pic_as_avatar || false);

      // Update localStorage with fresh data
      const updatedUserData = {
        ...getUserData(portal),
        ...userData,
        profile_photo: photoUrl,
        profile_pic_url: photoUrl,
      };
      const refreshToken = localStorage.getItem(`${portal}_refresh_token`) || '';
      setAuthTokens(portal, token, refreshToken, updatedUserData);

      setIsLoading(false);
    } catch (error) {
      console.error('Error loading profile:', error);
      // Fallback to localStorage if API fails
      const userData = getUserData(portal);
      if (userData) {
        let photoUrl = null;
        const photoField = userData.profile_photo || userData.profile_pic_url || userData.photo || userData.avatar;
        if (photoField) {
          if (photoField.startsWith('http://') || photoField.startsWith('https://')) {
            photoUrl = photoField;
          } else {
            const cleanPath = photoField.startsWith('/') ? photoField.slice(1) : photoField;
            photoUrl = apiUrl(cleanPath);
          }
        }
        const profile = {
          photo: photoUrl,
          fullName: userData.full_name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.username || 'User',
          username: userData.username?.includes('__') ? userData.username.split('__')[1] : (userData.username || ''),
          schoolName: userData.school?.school_name || userData.school_name || null,
          syllabus: userData.board?.board_name || userData.syllabus || null,
          grade: userData.grade?.grade_name || userData.grade_name || null,
          division: userData.division?.division_name || userData.division_display?.division_name || null,
          userId: userData.id || userData.user_id || null,
          useProfilePicAsAvatar: userData.use_profile_pic_as_avatar || false,
          userType: userData.user_type || userData.userType || null
        };
        setUserProfile(profile);
        setFullName(profile.fullName);
        setProfilePhotoPreview(photoUrl);
        setUseProfilePicAsAvatar(userData.use_profile_pic_as_avatar || false);
      }
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
    // Only load inventory for students
    const userData = getUserData(portal);
    if (userData?.user_type === 'student' || userData?.userType === 'student') {
      loadInventory();
    }

    // Listen for profile updates to refresh data
    const handleProfileUpdate = () => {
      loadProfile();
    };

    window.addEventListener('profileUpdated', handleProfileUpdate);
    window.addEventListener('storage', handleProfileUpdate);

    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdate);
      window.removeEventListener('storage', handleProfileUpdate);
    };
  }, [portal]);

  // Preference is now loaded from backend in loadProfile, but sync if profile pic is removed
  useEffect(() => {
    // If no profile pic exists but preference is true, clear it
    if (!profilePhotoPreview && useProfilePicAsAvatar) {
      setUseProfilePicAsAvatar(false);
      // Also update backend
      const updatePreference = async () => {
        try {
          const token = getAuthToken(portal);
          if (token) {
            await axios.patch(
              apiUrl('users/profile/'),
              { use_profile_pic_as_avatar: false },
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            );
          }
        } catch (error) {
          console.error('Error updating preference:', error);
        }
      };
      updatePreference();
    }
  }, [portal, profilePhotoPreview, useProfilePicAsAvatar]);

  // Load inventory and equipped items
  const loadInventory = async () => {
    setLoadingInventory(true);
    try {
      const inventoryResponse = await shopAPI.getInventory();
      const inventoryItems = Array.isArray(inventoryResponse) ? inventoryResponse : (inventoryResponse.results || inventoryResponse);
      setInventory(inventoryItems);

      // Get equipped items
      const equipped = {
        CHARACTER: inventoryItems.find(inv => inv.is_equipped && inv.shop_item?.item_type === 'CHARACTER') || null,
        AVATAR: inventoryItems.find(inv => inv.is_equipped && inv.shop_item?.item_type === 'AVATAR') || null,
        FRAME: inventoryItems.find(inv => inv.is_equipped && inv.shop_item?.item_type === 'FRAME') || null,
      };
      setEquippedItems(equipped);

      // Debug: Log inventory items to verify structure
      console.log('Inventory items:', inventoryItems);
      console.log('Avatar items:', inventoryItems.filter(inv => inv.shop_item?.item_type === 'AVATAR'));
      console.log('Frame items:', inventoryItems.filter(inv => inv.shop_item?.item_type === 'FRAME'));
      console.log('Character items:', inventoryItems.filter(inv => inv.shop_item?.item_type === 'CHARACTER'));
    } catch (err) {
      console.error('Error loading inventory:', err);
    } finally {
      setLoadingInventory(false);
    }
  };

  // Check if student has purchased items in each category
  // An item is considered "purchased" if it exists in inventory (regardless of whether it was free or paid)
  // We only exclude the default items that shouldn't be in the shop
  const defaultItemNames = ['Default Budlee AI Character', 'Default Student Avatar', 'Default Profile Frame'];

  const hasPurchasedAvatar = inventory.some(inv => {
    if (!inv.shop_item) return false;
    const itemName = inv.shop_item.item_name;
    const itemType = inv.shop_item.item_type;
    const isDefaultItem = defaultItemNames.includes(itemName);
    // Check if it's an AVATAR type and not a default item
    const result = itemType === 'AVATAR' && !isDefaultItem;
    if (result) {
      console.log('[ProfilePage] Found purchased Avatar:', { itemName, itemType, isDefaultItem });
    }
    return result;
  });

  const hasPurchasedFrame = inventory.some(inv => {
    if (!inv.shop_item) return false;
    const itemName = inv.shop_item.item_name;
    const itemType = inv.shop_item.item_type;
    const isDefaultItem = defaultItemNames.includes(itemName);
    // Check if it's a FRAME type and not a default item
    const result = itemType === 'FRAME' && !isDefaultItem;
    if (result) {
      console.log('[ProfilePage] Found purchased Frame:', { itemName, itemType, isDefaultItem });
    }
    return result;
  });

  const hasPurchasedCharacter = inventory.some(inv => {
    if (!inv.shop_item) return false;
    const itemName = inv.shop_item.item_name;
    const itemType = inv.shop_item.item_type;
    const isDefaultItem = defaultItemNames.includes(itemName);
    // Check if it's a CHARACTER type and not a default item
    const result = itemType === 'CHARACTER' && !isDefaultItem;
    if (result) {
      console.log('[ProfilePage] Found purchased Character:', { itemName, itemType, isDefaultItem });
    }
    return result;
  });

  // Debug: Log purchase status
  useEffect(() => {
    console.log('[ProfilePage] Purchase status check:', {
      hasPurchasedAvatar,
      hasPurchasedFrame,
      hasPurchasedCharacter,
      inventoryCount: inventory.length,
      inventoryItems: inventory.map(inv => ({
        id: inv.id,
        itemName: inv.shop_item?.item_name,
        itemType: inv.shop_item?.item_type,
        isEquipped: inv.is_equipped
      }))
    });
  }, [inventory, hasPurchasedAvatar, hasPurchasedFrame, hasPurchasedCharacter]);

  // Check if student has any purchased items (for showing the message)
  const hasPurchasedItems = hasPurchasedAvatar || hasPurchasedFrame || hasPurchasedCharacter;

  // Handle equip/unequip
  const handleEquipItem = async (inventoryItemId, itemType) => {
    if (equipping) return;

    setEquipping(inventoryItemId);

    try {
      await shopAPI.equipItem(inventoryItemId);

      // Refresh inventory
      await loadInventory();

      // Trigger events to update sidebar and other components
      window.dispatchEvent(new Event('storage'));
      window.dispatchEvent(new CustomEvent('equippedItemsUpdated'));

      toast.success('Item applied successfully!');
    } catch (err) {
      console.error('Error equipping item:', err);
      toast.error(err.message || 'Failed to equip item. Please try again.');
    } finally {
      setEquipping(null);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setFullName(userProfile?.fullName || '');
    setProfilePhoto(null);
    setProfilePhotoPreview(userProfile?.photo || null);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      setProfilePhoto(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!fullName.trim()) {
      toast.error('Full name cannot be empty');
      return;
    }

    setIsSaving(true);
    try {
      const token = getAuthToken(portal);
      if (!token) {
        throw new Error('Authentication required');
      }

      const formData = new FormData();
      formData.append('full_name', fullName.trim());
      if (profilePhoto) {
        formData.append('profile_photo', profilePhoto);
      }

      // Update profile via backend API
      // Try PATCH first, fallback to PUT if needed
      // Note: Backend may need to implement PATCH/PUT for profile updates
      let response;
      try {
        response = await axios.patch(
          apiUrl('users/profile/'),
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } catch (patchError) {
        // If PATCH doesn't work, try PUT
        if (patchError.response?.status === 405 || patchError.response?.status === 404) {
          try {
            response = await axios.put(
              apiUrl('users/profile/'),
              formData,
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'multipart/form-data'
                }
              }
            );
          } catch (putError) {
            // If both fail, update local storage only (for now until backend supports it)
            console.warn('Backend profile update endpoint not available, updating local storage only');
            response = { data: { profile_photo: profilePhotoPreview, full_name: fullName.trim() } };
          }
        } else {
          throw patchError;
        }
      }

      // Reload user profile from backend to get updated data
      try {
        const profileResponse = await axios.get(
          apiUrl('users/profile/'),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        // Update local storage with fresh data from backend
        // Backend returns profile_pic_url, not profile_photo
        const profilePicUrl = profileResponse.data?.profile_pic_url || response.data?.profile_pic_url;
        const updatedUserData = {
          ...getUserData(portal),
          ...profileResponse.data,
          full_name: fullName.trim(),
          first_name: fullName.trim().split(' ')[0] || '',
          last_name: fullName.trim().split(' ').slice(1).join(' ') || '',
          profile_photo: profilePicUrl, // Store as profile_photo for compatibility
          profile_pic_url: profilePicUrl, // Also store as profile_pic_url
        };

        // Get refresh token if available
        const refreshToken = localStorage.getItem(`${portal}_refresh_token`) || '';
        setAuthTokens(portal, token, refreshToken, updatedUserData);

        // Get updated profile photo URL
        let updatedPhotoUrl = null;
        if (profilePicUrl) {
          if (profilePicUrl.startsWith('http://') || profilePicUrl.startsWith('https://')) {
            updatedPhotoUrl = profilePicUrl;
          } else {
            // If it's a relative path, construct full URL
            const cleanPath = profilePicUrl.startsWith('/') ? profilePicUrl.slice(1) : profilePicUrl;
            updatedPhotoUrl = apiUrl(cleanPath);
          }
        } else if (profilePhotoPreview) {
          // Fallback to preview if no URL from backend
          updatedPhotoUrl = profilePhotoPreview;
        }

        // Update local state with fresh data
        setUserProfile({
          ...userProfile,
          fullName: fullName.trim(),
          photo: updatedPhotoUrl
        });
        setProfilePhotoPreview(updatedPhotoUrl);

        // Trigger storage event to update sidebar and other components
        // Use a custom event that will trigger a reload
        window.dispatchEvent(new Event('storage'));
        window.dispatchEvent(new CustomEvent('profileUpdated'));
      } catch (profileError) {
        // If profile fetch fails, use response data or preview
        const profilePicUrl = response.data?.profile_pic_url || response.data?.profile_photo;
        const updatedUserData = {
          ...getUserData(portal),
          full_name: fullName.trim(),
          first_name: fullName.trim().split(' ')[0] || '',
          last_name: fullName.trim().split(' ').slice(1).join(' ') || '',
          profile_photo: profilePicUrl || profilePhotoPreview,
          profile_pic_url: profilePicUrl || profilePhotoPreview,
        };

        const refreshToken = localStorage.getItem(`${portal}_refresh_token`) || '';
        setAuthTokens(portal, token, refreshToken, updatedUserData);

        // Construct URL properly
        let photoUrl = null;
        if (profilePicUrl) {
          if (profilePicUrl.startsWith('http://') || profilePicUrl.startsWith('https://')) {
            photoUrl = profilePicUrl;
          } else {
            const cleanPath = profilePicUrl.startsWith('/') ? profilePicUrl.slice(1) : profilePicUrl;
            photoUrl = apiUrl(cleanPath);
          }
        } else if (profilePhotoPreview) {
          photoUrl = profilePhotoPreview;
        }

        setUserProfile({
          ...userProfile,
          fullName: fullName.trim(),
          photo: photoUrl
        });
        setProfilePhotoPreview(photoUrl);
      }

      setProfilePhoto(null);
      setIsEditing(false);

      // Reload profile data to ensure everything is in sync
      // Small delay to ensure backend has processed the update
      setTimeout(() => {
        loadProfile();
      }, 500);

      // Trigger storage event to update sidebar and other components
      window.dispatchEvent(new Event('storage'));
      window.dispatchEvent(new CustomEvent('profileUpdated'));

      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to update profile';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await clearAllSessions();
      navigate('/login');
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Error during logout:', error);
      toast.error('Failed to logout');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-[hsl(var(--main-bg))]">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--green-bright))]" />
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="flex items-center justify-center h-full bg-[hsl(var(--main-bg))]">
        <p className="text-muted-foreground">No profile data available</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] p-3 sm:p-6 overflow-y-auto">
      <div className="max-w-4xl mx-auto w-full space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="text-center lg:text-left relative">
          <div className="flex items-center justify-center lg:justify-start gap-3 sm:gap-4">
            <User className="w-6 h-6 sm:w-8 sm:h-8 text-[hsl(var(--green-bright))]" />
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">Profile</h1>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Manage your profile information</p>
          
          {/* Mobile Logout Button - Icon Only */}
          <div className="lg:hidden absolute top-0 right-0">
            <Button
              onClick={() => setShowLogoutModal(true)}
              variant="outline"
              size="sm"
              className="p-2 border-red-500/30 text-red-500 hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors duration-200"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Profile Card */}
        <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
          <CardHeader>
            <CardTitle className="text-foreground text-base sm:text-lg">Profile Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5 sm:space-y-6">
            {/* Profile Layout - Grid with 2 columns to reduce height */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {/* Left Column: Profile Photo */}
              <div className="flex flex-col items-center space-y-4">
                <div className="relative">
                  {/* Profile Photo with Frame */}
                  <div className="relative w-32 h-32">
                    {/* Avatar/Profile Photo - Base Layer */}
                    {/* Priority: If useProfilePicAsAvatar is true and profile pic exists → Profile Pic, else → Equipped Avatar, else → Default Icon */}
                    <div className="absolute inset-0 w-full h-full rounded-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] flex items-center justify-center overflow-hidden z-0" style={{ clipPath: 'circle(50% at 50% 50%)' }}>
                      {(() => {
                        const imageToShow = (useProfilePicAsAvatar && profilePhotoPreview)
                          ? profilePhotoPreview
                          : (equippedItems.AVATAR?.shop_item?.image_url_display || null);

                        return imageToShow ? (
                          <img
                            src={imageToShow}
                            alt="Profile"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                        ) : null;
                      })()}
                      <div className={cn(
                        "absolute inset-0 w-full h-full items-center justify-center",
                        ((useProfilePicAsAvatar && profilePhotoPreview) || equippedItems.AVATAR?.shop_item?.image_url_display) ? "hidden" : "flex"
                      )}>
                        <User className="w-16 h-16 text-white" />
                      </div>
                    </div>
                    {/* Profile Frame Overlay - Top Layer - Larger and extending outside */}
                    {equippedItems.FRAME?.shop_item?.image_url_display && (
                      <div className="absolute -inset-4 rounded-full overflow-visible pointer-events-none z-10" style={{ clipPath: 'circle(50% at 50% 50%)' }}>
                        <img
                          src={equippedItems.FRAME.shop_item.image_url_display}
                          alt="Frame"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}
                    {/* Camera Button - Only visible in edit mode */}
                    {isEditing && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="absolute -bottom-1 -right-1 p-2 bg-[hsl(var(--green-bright))] rounded-full hover:bg-[hsl(var(--teal-vivid))] transition-colors shadow-lg z-20 border-2 border-[hsl(var(--main-bg))]"
                        title="Change profile photo"
                      >
                        <Camera className="w-4 h-4 text-white" />
                      </button>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                </div>
                {isEditing && (
                  <p className="text-xs text-muted-foreground text-center">
                    Click the camera icon to change your profile photo
                  </p>
                )}
                {/* Edit Profile Button - Below profile icon */}
                {!isEditing && (
                  <Button
                    onClick={handleEditClick}
                    className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white hover:from-[hsl(var(--teal-vivid))] hover:to-[hsl(var(--green-bright))]"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                )}
              </div>

              {/* Right Column: Profile Information */}
              <div className="space-y-4">
                {/* Full Name and Username - Same row */}
                <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4">
                  {/* Full Name - Editable */}
                  <div className="text-left">
                    <Label htmlFor="fullName" className="text-[hsl(var(--muted-foreground))] text-sm">Full Name</Label>
                    {isEditing ? (
                      <Input
                        id="fullName"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="mt-2 bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] text-white"
                        placeholder="Enter your full name"
                      />
                    ) : (
                      <p className="mt-2 text-white font-medium">{userProfile.fullName}</p>
                    )}
                  </div>

                  {/* Username - Read-only */}
                  <div className="text-left">
                    <Label className="text-[hsl(var(--muted-foreground))] text-sm">Username</Label>
                    <p className="mt-2 text-white">{userProfile.username}</p>
                  </div>
                </div>

                {/* School, Syllabus, Grade - Grid layout */}
                <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4">
                  {/* School - Read-only */}
                  {userProfile.schoolName && (
                    <div className="text-left">
                      <Label className="text-[hsl(var(--muted-foreground))] text-sm">School</Label>
                      <p className="mt-2 text-white">{userProfile.schoolName}</p>
                    </div>
                  )}

                  {/* Syllabus - Read-only */}
                  {userProfile.syllabus && (
                    <div className="text-left">
                      <Label className="text-[hsl(var(--muted-foreground))] text-sm">Syllabus</Label>
                      <p className="mt-2 text-white">{userProfile.syllabus}</p>
                    </div>
                  )}

                  {/* Grade - Read-only */}
                  {userProfile.grade && (
                    <div className="text-left">
                      <Label className="text-[hsl(var(--muted-foreground))] text-sm">Grade</Label>
                      <p className="mt-2 text-white">{userProfile.grade}</p>
                    </div>
                  )}

                  {/* Division - Read-only */}
                  {userProfile.division && (
                    <div className="text-left">
                      <Label className="text-[hsl(var(--muted-foreground))] text-sm">Division</Label>
                      <p className="mt-2 text-white">{userProfile.division}</p>
                    </div>
                  )}
                </div>

                {/* Email and Phone - Grid layout with editable fields for admin */}
                <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4 pt-4 border-t border-[hsl(var(--card-border))]">
                  {/* Email */}
                  <div className="text-left">
                    <Label className="text-[hsl(var(--muted-foreground))] text-sm">Email</Label>
                    {!isEditing ? (
                      <p className="mt-2 text-white">{userProfile.email || '—'}</p>
                    ) : (
                      <Input
                        type="email"
                        value={userProfile.email || ''}
                        onChange={(e) => setUserProfile({ ...userProfile, email: e.target.value })}
                        className="mt-2 bg-[hsl(var(--input-bg))] text-white"
                        placeholder="Email"
                      />
                    )}
                  </div>

                  {/* Phone */}
                  <div className="text-left">
                    <Label className="text-[hsl(var(--muted-foreground))] text-sm">Phone</Label>
                    {!isEditing ? (
                      <p className="mt-2 text-white">{userProfile.phone_no || '—'}</p>
                    ) : (
                      <Input
                        type="tel"
                        value={userProfile.phone_no || ''}
                        onChange={(e) => setUserProfile({ ...userProfile, phone_no: e.target.value })}
                        className="mt-2 bg-[hsl(var(--input-bg))] text-white"
                        placeholder="Phone"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Equipped Items Selection - Only for students */}
            {!isEditing && userProfile?.userType === 'student' && (
              <div className="space-y-4 pt-4 border-t border-[hsl(var(--card-border))]">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-foreground">Customize Appearance</h3>
                </div>
                {!hasPurchasedItems ? (
                  <div className="text-sm text-muted-foreground bg-[hsl(var(--card-bg))] p-3 rounded-lg border border-[hsl(var(--card-border))] space-y-3">
                    <p>
                      Purchase items from the Shop to customize your appearance. Visit the Shop to unlock avatars, frames, and characters!
                    </p>
                    <Button
                      onClick={() => navigate('/student/achievements/shop')}
                      className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white hover:from-[hsl(var(--teal-vivid))] hover:to-[hsl(var(--green-bright))]"
                      size="sm"
                    >
                      <ShoppingBag className="w-4 h-4 mr-2" />
                      Visit the Shop
                    </Button>
                  </div>
                ) : (
                  /* Customize Options - Grid layout with 3 columns */
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {/* Avatar Selection */}
                    <div className="text-left">
                      <Label className="text-[hsl(var(--muted-foreground))] mb-2 block">Avatar</Label>
                      <Select
                        value={useProfilePicAsAvatar && profilePhotoPreview ? 'profile_pic' : (equippedItems.AVATAR?.id?.toString() || 'default')}
                        onValueChange={async (value) => {
                          const token = getAuthToken(portal);
                          if (value === 'profile_pic') {
                            // Use profile pic as avatar
                            setUseProfilePicAsAvatar(true);
                            // Save to backend
                            try {
                              if (token) {
                                await axios.patch(
                                  apiUrl('users/profile/'),
                                  { use_profile_pic_as_avatar: true },
                                  {
                                    headers: {
                                      'Authorization': `Bearer ${token}`,
                                      'Content-Type': 'application/json'
                                    }
                                  }
                                );
                              }
                            } catch (error) {
                              console.error('Error saving preference:', error);
                            }
                            // Trigger event to update sidebar
                            window.dispatchEvent(new Event('storage'));
                            window.dispatchEvent(new CustomEvent('profileUpdated'));
                          } else if (value === 'default') {
                            // Use default avatar
                            setUseProfilePicAsAvatar(false);
                            // Save to backend
                            try {
                              if (token) {
                                await axios.patch(
                                  apiUrl('users/profile/'),
                                  { use_profile_pic_as_avatar: false },
                                  {
                                    headers: {
                                      'Authorization': `Bearer ${token}`,
                                      'Content-Type': 'application/json'
                                    }
                                  }
                                );
                              }
                            } catch (error) {
                              console.error('Error saving preference:', error);
                            }
                            // Unequip current avatar if any
                            if (equippedItems.AVATAR) {
                              await handleEquipItem(equippedItems.AVATAR.id, 'AVATAR');
                            } else {
                              // Trigger event to update sidebar even if no avatar to unequip
                              window.dispatchEvent(new Event('storage'));
                              window.dispatchEvent(new CustomEvent('profileUpdated'));
                            }
                          } else {
                            // Use equipped avatar
                            setUseProfilePicAsAvatar(false);
                            // Save to backend
                            try {
                              if (token) {
                                await axios.patch(
                                  apiUrl('users/profile/'),
                                  { use_profile_pic_as_avatar: false },
                                  {
                                    headers: {
                                      'Authorization': `Bearer ${token}`,
                                      'Content-Type': 'application/json'
                                    }
                                  }
                                );
                              }
                            } catch (error) {
                              console.error('Error saving preference:', error);
                            }
                            const inventoryItem = inventory.find(inv => inv.id === parseInt(value));
                            if (inventoryItem) {
                              await handleEquipItem(inventoryItem.id, 'AVATAR');
                            }
                          }
                        }}
                        disabled={loadingInventory || equipping !== null || !hasPurchasedAvatar}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Default Avatar</SelectItem>
                          {profilePhotoPreview && (
                            <SelectItem value="profile_pic">
                              Your Profile Pic {useProfilePicAsAvatar && '(Selected)'}
                            </SelectItem>
                          )}
                          {inventory
                            .filter(inv => inv.shop_item?.item_type === 'AVATAR')
                            .map(inv => (
                              <SelectItem key={inv.id} value={inv.id.toString()}>
                                {inv.shop_item?.item_name || 'Avatar'}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Profile Frame Selection */}
                    <div className="text-left">
                      <Label className="text-[hsl(var(--muted-foreground))] mb-2 block">Profile Frame</Label>
                      <Select
                        value={equippedItems.FRAME?.id?.toString() || 'default'}
                        onValueChange={(value) => {
                          if (value === 'default') {
                            if (equippedItems.FRAME) {
                              handleEquipItem(equippedItems.FRAME.id, 'FRAME');
                            }
                          } else {
                            const inventoryItem = inventory.find(inv => inv.id === parseInt(value));
                            if (inventoryItem) {
                              handleEquipItem(inventoryItem.id, 'FRAME');
                            }
                          }
                        }}
                        disabled={loadingInventory || equipping !== null || !hasPurchasedFrame}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Default Frame</SelectItem>
                          {inventory
                            .filter(inv => inv.shop_item?.item_type === 'FRAME')
                            .map(inv => (
                              <SelectItem key={inv.id} value={inv.id.toString()}>
                                {inv.shop_item?.item_name || 'Frame'}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Budlee AI Character Selection */}
                    <div className="text-left">
                      <Label className="text-[hsl(var(--muted-foreground))] mb-2 block">Budlee AI Character</Label>
                      <Select
                        value={equippedItems.CHARACTER?.id?.toString() || 'default'}
                        onValueChange={(value) => {
                          if (value === 'default') {
                            if (equippedItems.CHARACTER) {
                              handleEquipItem(equippedItems.CHARACTER.id, 'CHARACTER');
                            }
                          } else {
                            const inventoryItem = inventory.find(inv => inv.id === parseInt(value));
                            if (inventoryItem) {
                              handleEquipItem(inventoryItem.id, 'CHARACTER');
                            }
                          }
                        }}
                        disabled={loadingInventory || equipping !== null || !hasPurchasedCharacter}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Default Character</SelectItem>
                          {inventory
                            .filter(inv => inv.shop_item?.item_type === 'CHARACTER')
                            .map(inv => (
                              <SelectItem key={inv.id} value={inv.id.toString()}>
                                {inv.shop_item?.item_name || 'Character'}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons - Only shown in edit mode */}
            {isEditing && (
              <div className="flex justify-center sm:justify-start gap-3 pt-4">
                <Button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white hover:from-[hsl(var(--teal-vivid))] hover:to-[hsl(var(--green-bright))]"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  variant="outline"
                  className="border-[hsl(var(--card-border))] text-white hover:bg-[hsl(var(--sidebar-hover))] hover:text-white"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Logout Confirmation Modal */}
      <ConfirmationModal
        isOpen={showLogoutModal}
        onOpenChange={setShowLogoutModal}
        title="Log out"
        description="You will be signed out of the student portal and returned to login."
        confirmText="Log out"
        cancelText="Stay logged in"
        onConfirm={handleLogout}
        onCancel={() => setShowLogoutModal(false)}
        zIndex="z-[10000]"
      />
    </div>
  );
}

